import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  UserPlus, 
  Smartphone, 
  Bell, 
  Heart,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  MessageCircle,
  Pill,
  Utensils,
  Activity,
  Copy,
  Download,
  Share2,
  MoreVertical,
  Plus,
  Home,
  Users,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Logo Component
const Logo = ({ className = "h-12" }) => (
  <img 
    src="/logo-curacheck-new.png"
    alt="CuraCheck" 
    className={`${className} object-contain`}
  />
);

// Tab Selector Component
const TabSelector = ({ activeTab, setActiveTab }) => (
  <div className="flex bg-[#F0F4F1] rounded-2xl p-1.5 max-w-md mx-auto">
    <button
      onClick={() => setActiveTab('familiare')}
      className={`flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
        activeTab === 'familiare'
          ? 'bg-[#4A7C59] text-white shadow-lg'
          : 'text-[#64748B] hover:text-[#2C3E50]'
      }`}
    >
      <Heart className="w-4 h-4" />
      Per il Familiare
    </button>
    <button
      onClick={() => setActiveTab('assistente')}
      className={`flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
        activeTab === 'assistente'
          ? 'bg-[#5DADE2] text-white shadow-lg'
          : 'text-[#64748B] hover:text-[#2C3E50]'
      }`}
    >
      <Users className="w-4 h-4" />
      Per l'Assistente
    </button>
  </div>
);

// Familiare Section
const FamiliareSection = () => {
  const [copied, setCopied] = useState(false);
  
  const accessLinkMessage = `Ciao! 👋

Ti mando il link per CuraCheck, l'app per registrare le attività di [NOME].

📱 Clicca qui per aprire:
[INSERISCI IL TUO LINK]

È semplicissimo:
1. Apri il link e salva l'app nella Home
2. Premi i pulsanti colorati per registrare le attività
3. Io ricevo subito la notifica push

Grazie mille! 💚`;

  const copyMessage = () => {
    navigator.clipboard.writeText(accessLinkMessage);
    setCopied(true);
    toast.success('Messaggio copiato!');
    setTimeout(() => setCopied(false), 2000);
  };

  const steps = [
    {
      number: 1,
      title: "Registrati",
      description: "Clicca su 'Accedi con Google' oppure registrati con email e password. In pochi secondi sei dentro!",
      icon: UserPlus,
      color: "#4A7C59",
      bgColor: "#E8F5E9"
    },
    {
      number: 2,
      title: "Inserisci i dati",
      description: "Scrivi il nome del tuo caro e configura il profilo dell'assistito.",
      icon: Smartphone,
      color: "#5DADE2",
      bgColor: "#E3F2FD"
    },
    {
      number: 3,
      title: "Genera Link",
      description: "Il sistema genera un link unico da condividere con l'assistente per farla accedere all'app.",
      icon: Share2,
      color: "#F59E0B",
      bgColor: "#FEF3C7"
    },
    {
      number: 4,
      title: "Ricevi Notifiche Push istantanee",
      description: "Ogni volta che l'assistente registra un'attività, ricevi subito una notifica push sul tuo smartphone!",
      icon: Bell,
      color: "#E91E63",
      bgColor: "#FCE4EC"
    }
  ];

  return (
    <div className="space-y-8">
      {/* Steps */}
      <div className="grid gap-4">
        {steps.map((step) => (
          <div 
            key={step.number}
            className="bg-white rounded-2xl p-5 border border-[#E5E7EB] shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-start gap-4">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: step.bgColor }}
              >
                <step.icon className="w-7 h-7" style={{ color: step.color }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span 
                    className="w-6 h-6 rounded-full text-white text-sm font-bold flex items-center justify-center"
                    style={{ backgroundColor: step.color }}
                  >
                    {step.number}
                  </span>
                  <h3 className="font-bold text-[#2C3E50]">{step.title}</h3>
                </div>
                <p className="text-[#64748B] text-sm">{step.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Copy Message Tool */}
      <div className="bg-gradient-to-br from-[#E8F5E9] to-[#F0F9F1] rounded-2xl p-6 border border-[#C8E6C9]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#4A7C59] rounded-xl flex items-center justify-center">
            <Share2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#2C3E50]">Messaggio pronto da condividere</h3>
            <p className="text-sm text-[#64748B]">Copia e incolla per invitare l'assistente</p>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-[#E5E7EB] mb-4">
          <pre className="text-sm text-[#2C3E50] whitespace-pre-wrap font-sans">
            {accessLinkMessage}
          </pre>
        </div>
        
        <Button
          onClick={copyMessage}
          className={`w-full h-12 rounded-xl font-semibold transition-all ${
            copied 
              ? 'bg-[#4A7C59] hover:bg-[#3A6345]' 
              : 'bg-[#4A7C59] hover:bg-[#3A6345]'
          } text-white`}
        >
          {copied ? (
            <>
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Copiato!
            </>
          ) : (
            <>
              <Copy className="w-5 h-5 mr-2" />
              Copia Link di Accesso
            </>
          )}
        </Button>
      </div>

      {/* Tutorial Dettagliato CuraCheck */}
      <div className="bg-white rounded-3xl p-6 border border-[#E5E7EB] shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-[#4A7C59] to-[#6B9F7D] rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#2C3E50]">Tutorial CuraCheck</h3>
            <p className="text-sm text-[#64748B]">Guida visuale passo dopo passo</p>
          </div>
        </div>

        {/* Tutorial Items with Screenshots */}
        <div className="space-y-6">
          
          {/* 1. Aggiungere un secondo assistito */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#4A7C59] to-[#6B9F7D] p-4 text-white">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <h4 className="font-bold">Aggiungere un secondo assistito</h4>
              </div>
              <p className="text-white/80 text-sm mt-1">Solo per utenti Premium - gestisci fino a 2 assistiti</p>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <img 
                  src="/tutorial/add-assistito.png" 
                  alt="Aggiungere un secondo assistito" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-[#64748B] mt-3">
                Dalla Dashboard, clicca sul pulsante <strong>"+ Aggiungi"</strong> nella card "Assistiti Monitorati" per aggiungerne un secondo.
              </p>
            </div>
          </div>

          {/* 2. Aggiungere notifiche a un'altra persona */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#5DADE2] to-[#85C1E9] p-4 text-white">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <h4 className="font-bold">Aggiungere notifiche a un'altra persona</h4>
              </div>
              <p className="text-white/80 text-sm mt-1">Condividi le notifiche con un familiare</p>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <img 
                  src="/tutorial/impostazioni.png" 
                  alt="Impostazioni nucleo familiare" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-[#64748B] mt-3">
                Vai nelle <strong>Impostazioni</strong> e trova la sezione "Nucleo Familiare". Aggiungi altri familiari per condividere le notifiche.
              </p>
            </div>
          </div>

          {/* 3. Mandare il link all'assistente */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] p-4 text-white">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <h4 className="font-bold">Mandare il link all'assistente</h4>
              </div>
              <p className="text-white/80 text-sm mt-1">Condividi il link per far accedere l'assistente</p>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <img 
                  src="/tutorial/link-assistente.png" 
                  alt="Link per l'assistente" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-[#64748B] mt-3">
                Nella Dashboard vai su <strong>"Link Assistente"</strong>. Prima configura le medicine, poi potrai copiare e condividere il link.
              </p>
            </div>
          </div>

          {/* 4. Aggiungere una medicina */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#E91E63] to-[#F06292] p-4 text-white">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <h4 className="font-bold">Aggiungere una medicina</h4>
              </div>
              <p className="text-white/80 text-sm mt-1">Configura il piano terapeutico</p>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <img 
                  src="/tutorial/add-medicine.png" 
                  alt="Aggiungere una medicina" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-[#64748B] mt-3">
                Vai nella sezione <strong>"Gestione Farmaci"</strong>, inserisci nome del farmaco, dosaggio e orario esatto della somministrazione.
              </p>
            </div>
          </div>

          {/* 5. Salvare alla Home */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#9C27B0] to-[#BA68C8] p-4 text-white">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">5</span>
                <h4 className="font-bold">Salvare CuraCheck nella Home</h4>
              </div>
              <p className="text-white/80 text-sm mt-1">Installa l'app come PWA per accesso rapido</p>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <img 
                  src="/tutorial/add-to-home.png" 
                  alt="Salvare alla Home" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-[#64748B] mt-3">
                <strong>iPhone:</strong> Safari → Condividi → "Aggiungi a Home"<br/>
                <strong>Android:</strong> Chrome → Menu (⋮) → "Aggiungi a schermata Home"
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <Link to="/registrazione">
          <Button className="h-14 px-8 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg shadow-lg">
            Inizia la Prova Gratuita
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
        <p className="text-sm text-[#64748B] mt-2">7 giorni gratis, nessuna carta richiesta</p>
      </div>
    </div>
  );
};

// Assistente Section
const AssistenteSection = () => {
  const downloadPDF = () => {
    // Download PDF guide
    const link = document.createElement('a');
    link.href = '/guida-assistente.pdf';
    link.download = 'Guida-CuraCheck-Assistente.pdf';
    link.click();
    toast.success('Download PDF avviato!');
  };

  const actionButtons = [
    { icon: Pill, label: "Routine", color: "#E3F2FD", iconColor: "#1976D2" },
    { icon: Utensils, label: "Pasti", color: "#FFF3E0", iconColor: "#E65100" },
    { icon: Heart, label: "Umore", color: "#FCE4EC", iconColor: "#C2185B" },
    { icon: Activity, label: "Attività", color: "#E8F5E9", iconColor: "#4A7C59" }
  ];

  return (
    <div className="space-y-8">
      {/* Hero Message */}
      <div className="bg-gradient-to-br from-[#5DADE2]/10 to-[#5DADE2]/5 rounded-2xl p-6 text-center border border-[#5DADE2]/20">
        <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-[#2C3E50] mb-2">Semplicissimo da usare!</h3>
        <p className="text-[#64748B]">
          Basta cliccare il link ricevuto dal familiare. 
          Salva l'app nella Home e sei pronta.
        </p>
      </div>

      {/* 4 Simple Steps */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-[#2C3E50] text-center">Come funziona</h3>
        
        <div className="grid gap-4">
          {/* Step 1 */}
          <div className="bg-white rounded-2xl p-5 border-2 border-[#5DADE2]/30 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#5DADE2] rounded-full flex items-center justify-center text-white font-bold text-xl">
                1
              </div>
              <div>
                <h4 className="font-bold text-[#2C3E50]">Apri il link ricevuto</h4>
                <p className="text-sm text-[#64748B]">Clicca sul link che ti ha inviato il familiare</p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-2xl p-5 border-2 border-[#F59E0B]/30 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F59E0B] rounded-full flex items-center justify-center text-white font-bold text-xl">
                2
              </div>
              <div>
                <h4 className="font-bold text-[#2C3E50]">Aggiungi a Home</h4>
                <p className="text-sm text-[#64748B]">Salva l'app sulla schermata Home del telefono</p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-2xl p-5 border-2 border-[#9C27B0]/30 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#9C27B0] rounded-full flex items-center justify-center text-white font-bold text-xl">
                3
              </div>
              <div>
                <h4 className="font-bold text-[#2C3E50]">Inquadra il QR code o apri il link diretto</h4>
                <p className="text-sm text-[#64748B]">Inquadra con la fotocamera il QR code della Stazione Digitale, oppure apri il link diretto ricevuto dalla famiglia.</p>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="bg-white rounded-2xl p-5 border-2 border-[#4A7C59]/30 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#4A7C59] rounded-full flex items-center justify-center text-white font-bold text-xl">
                4
              </div>
              <div>
                <h4 className="font-bold text-[#2C3E50]">Tocca "Dato" per confermare</h4>
                <p className="text-sm text-[#64748B]">Premi il pulsante dell'attività completata</p>
              </div>
            </div>
            
            {/* Preview Buttons */}
            <div className="grid grid-cols-4 gap-2 mt-4">
              {actionButtons.map((btn, i) => (
                <div 
                  key={i}
                  className="aspect-square rounded-xl flex flex-col items-center justify-center gap-1"
                  style={{ backgroundColor: btn.color }}
                >
                  <btn.icon className="w-6 h-6" style={{ color: btn.iconColor }} />
                  <span className="text-xs font-medium" style={{ color: btn.iconColor }}>{btn.label}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-4 bg-[#E8F5E9] rounded-xl p-3 flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-[#4A7C59]" />
              <span className="font-semibold text-[#4A7C59]">Il familiare riceve subito la notifica!</span>
            </div>
          </div>
        </div>
      </div>

      {/* PWA Instructions */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="bg-gradient-to-r from-[#2C3E50] to-[#34495E] p-4 text-white">
          <div className="flex items-center gap-3">
            <Home className="w-6 h-6" />
            <div>
              <h3 className="font-bold">Aggiungi alla schermata Home</h3>
              <p className="text-sm text-white/70">Per aprire CuraCheck come un'app vera</p>
            </div>
          </div>
        </div>
        
        <div className="p-5 space-y-4">
          {/* iOS Instructions */}
          <div className="bg-[#F8F9FA] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 bg-black rounded-md flex items-center justify-center">
                <span className="text-white text-xs font-bold">iOS</span>
              </div>
              <span className="font-semibold text-[#2C3E50]">iPhone / iPad</span>
            </div>
            <ol className="space-y-2 text-sm text-[#64748B]">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#5DADE2] rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <span>Tocca il pulsante <strong className="text-[#2C3E50]">Condividi</strong> (quadrato con freccia in su) in Safari</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#5DADE2] rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <span>Scorri e tocca <strong className="text-[#2C3E50]">"Aggiungi alla schermata Home"</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#5DADE2] rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <span>Tocca <strong className="text-[#2C3E50]">"Aggiungi"</strong> in alto a destra</span>
              </li>
            </ol>
            
            {/* Visual Guide iOS */}
            <div className="mt-3 flex items-center justify-center gap-2 text-[#5DADE2]">
              <div className="w-10 h-10 border-2 border-[#5DADE2] rounded-lg flex items-center justify-center">
                <Share2 className="w-5 h-5" />
              </div>
              <ArrowRight className="w-4 h-4" />
              <div className="w-10 h-10 border-2 border-[#5DADE2] rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5" />
              </div>
              <ArrowRight className="w-4 h-4" />
              <div className="w-10 h-10 border-2 border-[#4A7C59] bg-[#4A7C59] rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          {/* Android Instructions */}
          <div className="bg-[#F8F9FA] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 bg-[#3DDC84] rounded-md flex items-center justify-center">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              <span className="font-semibold text-[#2C3E50]">Android</span>
            </div>
            <ol className="space-y-2 text-sm text-[#64748B]">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#3DDC84] rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <span>Tocca i <strong className="text-[#2C3E50]">3 puntini</strong> in alto a destra in Chrome</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#3DDC84] rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <span>Tocca <strong className="text-[#2C3E50]">"Aggiungi a schermata Home"</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#3DDC84] rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <span>Tocca <strong className="text-[#2C3E50]">"Aggiungi"</strong></span>
              </li>
            </ol>
            
            {/* Visual Guide Android */}
            <div className="mt-3 flex items-center justify-center gap-2 text-[#3DDC84]">
              <div className="w-10 h-10 border-2 border-[#3DDC84] rounded-lg flex items-center justify-center">
                <MoreVertical className="w-5 h-5" />
              </div>
              <ArrowRight className="w-4 h-4" />
              <div className="w-10 h-10 border-2 border-[#3DDC84] rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5" />
              </div>
              <ArrowRight className="w-4 h-4" />
              <div className="w-10 h-10 border-2 border-[#4A7C59] bg-[#4A7C59] rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF */}
      <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-2xl p-6 border border-[#F59E0B]/30">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md">
            <Download className="w-7 h-7 text-[#F59E0B]" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-[#92400E]">Guida Rapida Stazione Digitale</h3>
            <p className="text-sm text-[#B45309]">Una pagina da tenere in casa come promemoria</p>
          </div>
        </div>
        
        <Button
          onClick={downloadPDF}
          className="w-full h-12 mt-4 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-xl font-semibold"
        >
          <Download className="w-5 h-5 mr-2" />
          Scarica Guida PDF
        </Button>
      </div>
    </div>
  );
};

// Main Tutorial Page
const TutorialPage = () => {
  const [activeTab, setActiveTab] = useState('familiare');

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-[#4A7C59] py-4 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <ArrowLeft className="w-5 h-5 text-white" />
            <Logo className="h-10" />
          </Link>
          <Link to="/login">
            <Button className="bg-white/20 hover:bg-white/30 text-white rounded-xl px-4">
              Accedi
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-2">
            Come usare CuraCheck
          </h1>
          <p className="text-[#64748B]">
            Guida semplice per famiglie e assistenti
          </p>
        </div>

        {/* Tab Selector */}
        <div className="mb-8">
          <TabSelector activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>

        {/* Content */}
        {activeTab === 'familiare' ? <FamiliareSection /> : <AssistenteSection />}
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 text-center text-sm text-[#64748B] border-t border-[#E5E7EB]">
        <p>© 2026 CuraCheck - A.M.D. GENERAL HOLDING S.R.L.S.</p>
      </footer>
    </div>
  );
};

export default TutorialPage;
